import React, { useRef } from "react";
import { Box, Button, Flex, Image, SimpleGrid, Text, Textarea, useToast } from "@chakra-ui/react";
import TextInput from "components/Inputs/TextInput";
import { useAppSelector } from "hooks/store/hooks";
import { Link, useNavigate, useParams } from "react-router-dom";
import { selectColorThemeMode, setLoader } from "stores/uiSlice";
import LeftArrow from "components/Icons/Common/LeftArrow.svg";
import { useEffect, useState } from "react";
import { getSingleProduct, saveProduct } from "api/products";
import { useDispatch } from "react-redux";
import { errorIdentifier } from "utils/errorHandler";
import { selectUserDetails } from "stores/authSlice";
import { selectUsersList, selectUsersLoaded, usersListThunk } from "stores/propertySlice";
import ReactSelect from "react-select";
import MultipleImagesComponent from "components/Media/MultipleImagesComponent";
import SingleImageComponent from "components/Media/SingleImageComponent";
import { getActiveCategories } from "api/categories/productCategories";
import { InfoOutlineIcon } from "@chakra-ui/icons";
import { getAllproductSubCategoriesFromCategory } from "api/categories/productSubCategories";
import DefaultAvatar from "components/Icons/UserPageIcons/DefaultAvatar.svg";

const customOptionLabel = ({ label, profileImage }) => (
    <div style={{ display: 'flex', alignItems: 'center' }}>
        <img src={profileImage ? `https://saatbaar-media.s3.us-east-2.amazonaws.com/${profileImage}` : DefaultAvatar} alt={label} style={{ width: 20, height: 20, marginRight: 10, borderRadius: "50%" }} />
        {label}
    </div>
);

const customFilterOption = (option, searchText) => {
    const label = option.data.label || '';
    return label.toLowerCase().includes(searchText.toLowerCase());
};

const ProductForm = () => {

    const singleSelectStyle = {
        control: (baseStyles) => ({
            ...baseStyles,
            borderColor: "#ccc",
            minHeight: "44px",
            borderRadius: "8px",
        }),
        placeholder: (baseStyles) => ({
            ...baseStyles,
            fontWeight: 500
        })
    };

    const { id } = useParams();
    const coverImageRef = useRef();
    const multipleImagesRef = useRef();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const toast = useToast();
    const usersList = useAppSelector(selectUsersList);
    const isUsersListLoaded = useAppSelector(selectUsersLoaded);
    const colorThemeMode = useAppSelector(selectColorThemeMode);
    const currentUserDetails = useAppSelector(selectUserDetails);
    const [formDataLoaded, setFormDataLoaded] = useState(false);
    const [coverImage, setCoverImage] = useState("");
    const [productImages, setProductImages] = useState([]);
    const [categories, setCategories] = useState([]);
    const [subCategories, setSubCategories] = useState([]);
    const [productForm, setProductForm] = useState({
        coverImage: "",
        productImages: [],
        name: "",
        price: null,
        mrpPrice: null,
        category: null,
        subCategory: null,
        description: "",
        rejectRemarks: "",
    })

    useEffect(() => {
        if (!isUsersListLoaded) {
            dispatch(usersListThunk());
        }

        getActiveCategories().then(res => {
            const categories = res.data.data.map(category => {
                return { ...category, label: category.category, value: category._id }
            });
            setCategories(categories)
        })

    }, [])

    useEffect(() => {
        if (id) {
            getSingleProduct(id).then(res => {
                setProductForm({ ...res.data.data.data })
                setCoverImage(res.data?.data?.data?.coverImage ?? "");
                setProductImages(res.data?.data?.data?.productImages ?? []);
                setFormDataLoaded(true);
            })
        }
    }, [id])

    useEffect(() => {

        if (categories.length && formDataLoaded && usersList.length) {

            const selectedCategory = categories.find(category => category._id === productForm.category)
            setProductForm(prevFrom => ({ ...prevFrom, category: selectedCategory }));

            getAllproductSubCategoriesFromCategory(selectedCategory._id).then(res => {
                const subCategories = res.data.data.map(category => {
                    return { ...category, label: category.subCategory, value: category._id }
                });
                setSubCategories(subCategories);

                if (productForm.subCategory) {
                    const selectedCategory = subCategories.find(category => category._id === productForm.subCategory)
                    setProductForm(prevFrom => ({ ...prevFrom, subCategory: selectedCategory }));
                }
            })
        }
    }, [formDataLoaded, usersList, categories])

    const handleProductFormChange = (key, value) => {
        debugger;

        setProductForm({ ...productForm, [key]: value })
    }

    const getProductSubCategoriesFromCategory = (categoryId) => {
        getAllproductSubCategoriesFromCategory(categoryId).then(res => {
            const subCategories = res.data.data.map(category => {
                return { ...category, label: category.subCategory, value: category._id }
            });
            setSubCategories(subCategories)
        })
    }

    const handleCategoryChange = (category) => {
        getProductSubCategoriesFromCategory(category._id);
        setProductForm({ ...productForm, category: category, subCategory: null })
    }

    const saveProductForm = () => {
        dispatch(setLoader(true));
        productForm.category = productForm.category._id;
        productForm.subCategory = productForm.subCategory?._id ?? null;
        productForm.coverImage = coverImage;
        productForm.productImages = productImages;
        if (id) productForm.approvalStatus = "Re-Submitted";
        if (id) productForm.rejectRemarks = "";
        if (id) productForm.live = false;

        saveProduct(productForm, id).then(async res => {
            await multipleImagesRef.current.triggerUpload();
            await coverImageRef.current.triggerUpload();
            dispatch(setLoader(false));
            navigate("/products", { replace: true });
            toast({
                title: "Success",
                description: id ? "Product Updated Succefully" : "Product Created Successfully",
                status: "success",
                duration: 4000,
                variant: "subtle",
                isClosable: true,
            });
        }).catch(err => {
            dispatch(setLoader(false));
            toast({
                title: "Error",
                description: errorIdentifier(err),
                status: "error",
                duration: 4000,
                variant: "subtle",
                isClosable: true,
            });
        })
    }

    return (
        <Box>
            <Box display={"flex"} justifyContent={"space-between"} alignItems={"center"} >
                <Box display={"flex"} alignItems={"center"}>
                    <Box as={Link} to={"/products"}>
                        <Image src={LeftArrow} mr={"10px"}></Image>
                    </Box>
                    <Text fontSize={"18px"} opacity={'50%'}> Products </Text>
                    <Text fontSize={"18px"} mx={3}> / </Text>
                    <Text as="span" fontSize={"18px"} fontWeight={500} opacity={'100%'}>  Create Product </Text>
                </Box>
                <Box display={"flex"} alignItems={"center"}>
                    <Button
                        p={"12px 24px"}
                        borderRadius={"30px"}
                        fontSize={"18px"}
                        fontWeight={500}
                        border={"2px solid"}
                        borderColor={`${colorThemeMode}_primary`}
                        color={`${colorThemeMode}_primary`}
                        lineHeight={"22px"}
                        height={"46px"}
                        onClick={() => navigate("/products", { replace: true })}
                    > Discard </Button>
                    <Button
                        height={"46px"}
                        ml={"10px"}
                        p={"12px 24px"}
                        borderRadius={"30px"}
                        fontSize={"18px"}
                        fontWeight={500}
                        color={"#fff"}
                        bgColor={`${colorThemeMode}_primary`}
                        onClick={() => saveProductForm()}
                    > Publish Product </Button>
                </Box>
            </Box>
            <Box
                bg={"#fff"}
                borderRadius={"12px"}
                boxShadow={"0px 8px 24px 0px rgba(0, 0, 0, 0.08)"}
                p={"24px"}
                mt={10}
            >
                {productForm.rejectRemarks && (
                    <Box
                        color="white"
                        p={3}
                        background={"#FFF0F0"}
                        borderRadius={"12px"}
                        display={"flex"}
                        mb={"24px"}
                    >
                        <InfoOutlineIcon w={6} h={6} mt={"2px"} color={"red"} />

                        <Box ml={"16px"}>
                            <Text
                                color={"#E43434"}
                                fontWeight={600}
                                fontSize={"18px"}
                                mb={"5px"}
                            >
                                Product has been Rejected Due to:
                            </Text>
                            <Text>{productForm.rejectRemarks}</Text>
                        </Box>
                    </Box>
                )}

                <Flex gap={5}>
                    <SingleImageComponent ref={coverImageRef} imgKey={productForm.coverImage} width={"15%"} height={"17vh"} updateImageFunction={setCoverImage} />
                    <MultipleImagesComponent ref={multipleImagesRef} height={"17vh"} title={"Product or Service Images"} imagesArray={productForm.productImages} updateImagesFunction={setProductImages} />
                </Flex>

                <SimpleGrid columns={1} spacing={4} mt={"45px"} >

                    <Box >
                        <Text variant={"secondary14"} mb={"6px"}> Product or Service Name </Text>
                        <TextInput
                            placeholder="Write Your Product Name"
                            value={productForm.name}
                            onChange={(e) => handleProductFormChange("name", e.target.value)}
                        />
                    </Box>

                </SimpleGrid>

                <SimpleGrid columns={2} spacing={4} mb={"24px"} mt={"30px"}>
                    <Box>
                        <Text variant={"secondary14"} mb={"6px"}> Price (₹) </Text>
                        <TextInput
                            type="number"
                            placeholder="Enter Price"
                            value={productForm.price}
                            onChange={(e) => handleProductFormChange("price", Number(e.target.value))}
                        />
                    </Box>
                    <Box>
                        <Text variant={"secondary14"} mb={"6px"}> MRP Price (₹) </Text>
                        <TextInput
                            type="number"
                            placeholder="Enter MRP Price"
                            value={productForm.mrpPrice}
                            onChange={(e) => handleProductFormChange("mrpPrice", Number(e.target.value))}
                        />
                    </Box>
                </SimpleGrid>

                <SimpleGrid columns={2} spacing={4} mb={"24px"}>
                    <Box>
                        <Text variant={"secondary14"} mb={"6px"}> Category </Text>
                        <ReactSelect
                            placeholder="Select Category"
                            options={categories}
                            value={productForm.category}
                            styles={singleSelectStyle}
                            onChange={(value) => { handleCategoryChange(value) }}
                        />
                    </Box>

                    <Box>
                        <Text variant={"secondary14"} mb={"6px"}> Sub Category </Text>
                        <ReactSelect
                            placeholder="Select Sub Category"
                            options={subCategories}
                            value={productForm.subCategory}
                            styles={singleSelectStyle}
                            onChange={(value) => { handleProductFormChange("subCategory", value) }}
                        />
                    </Box>


                </SimpleGrid>

                <Box mb={"24px"}>
                    <Text variant={"secondary14"} mb={"6px"}> Description </Text>
                    <Textarea
                        fontWeight={500}
                        border={"1px solid #CCC"}
                        _focusVisible={{ border: "1px solid #ccc" }}
                        borderRadius={"8px"}
                        placeholder="Write Your Product Description"
                        value={productForm.description}
                        onChange={(e) => handleProductFormChange("description", e.target.value)}
                        size="sm"
                    />
                </Box>

            </Box>
        </Box>
    );
}

export default ProductForm